// Student DashboardPage — welcome heading + active-enrollment overview.
import { useMemo } from "react";
import { useAuth } from "../../hooks/useAuth.js";
import { useFetch } from "../../hooks/useApi.js";
import StatCard from "../../components/shared/StatCard.js";
import Badge from "../../components/shared/Badge.js";
import type { Course, Enrollment, PaginatedResult } from "@dm-lms/shared/types";

export default function DashboardPage() {
  const { user } = useAuth();
  const { data: enrollmentsData, isLoading, error } = useFetch<PaginatedResult<Enrollment>>(
    "/api/enrollments/mine?page=1&pageSize=100"
  );
  const { data: coursesData } = useFetch<PaginatedResult<Course>>("/api/courses?page=1&pageSize=100");

  const courseById = useMemo(() => {
    const map = new Map<string, Course>();
    for (const course of coursesData?.items ?? []) map.set(course.id, course);
    return map;
  }, [coursesData]);

  // "Active" = enrolled but not yet completed.
  const activeEnrollments = useMemo(
    () => (enrollmentsData?.items ?? []).filter((enrollment) => enrollment.completedAt === null),
    [enrollmentsData]
  );

  return (
    <div className="dm-page">
      <h1 className="text-2xl font-semibold text-dm-navy mb-6">Welcome, {user?.name ?? "Student"}</h1>

      {isLoading && <p className="text-dm-slate text-sm">Loading your courses…</p>}
      {error && <p className="text-red-600 text-sm">{error}</p>}

      {!isLoading && !error && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <StatCard label="Active Courses" value={activeEnrollments.length} />
          </div>

          <h2 className="text-lg font-medium text-dm-navy mb-3">Your Courses</h2>
          {activeEnrollments.length === 0 ? (
            <p className="text-dm-slate text-sm">You are not enrolled in any active courses.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {activeEnrollments.map((enrollment) => {
                const course = courseById.get(enrollment.courseId);
                return (
                  <div key={enrollment.id} className="rounded-lg border border-gray-200 bg-white p-4">
                    <p className="font-medium text-dm-navy mb-2">{course?.title ?? "Course"}</p>
                    {course && <Badge tone="gray">{course.mode}</Badge>}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
